import React, { useState } from "react";
import { ArrowUpRight, Mail, CheckCircle2, AlertCircle, Loader2, Sparkles, MapPin } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { CONTACT, COMPANY } from "../data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  company: "",
  telephone: "",
  inquiry_type: "",
  subject: "",
  message: "",
};

// Basic RFC-5322-ish email check for client-side UX. Server is authoritative.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(form) {
  const errs = {};
  if (!form.name.trim()) errs.name = "Please enter your name.";
  if (!form.email.trim()) errs.email = "Please enter your email address.";
  else if (!EMAIL_RE.test(form.email.trim())) errs.email = "Enter a valid email address (e.g. name@company.com).";
  if (!form.message.trim()) errs.message = "Please add a short message.";
  else if (form.message.trim().length < 10) errs.message = "Message is a little short — add a few more details.";
  return errs;
}

// Extract the first useful error message from a FastAPI 422 response.
function parseServerErrors(data) {
  if (!data) return null;
  if (typeof data.detail === "string") return { _form: data.detail };
  if (Array.isArray(data.detail)) {
    const out = {};
    for (const d of data.detail) {
      const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : "_form";
      const msg = d.msg?.replace(/^Value error, /, "") || "Invalid value.";
      out[field] = msg;
    }
    return out;
  }
  return null;
}

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // {id, received_at} once submitted

  const change = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    const errs = validate(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form, {
        headers: { "Content-Type": "application/json" },
        timeout: 15000,
      });
      setSuccess({ id: data.id, received_at: data.received_at });
      setForm(EMPTY);
      toast.success("Inquiry received. Thank you.");
    } catch (err) {
      // Prefer server-provided per-field errors.
      const status = err?.response?.status;
      const serverErrs = parseServerErrors(err?.response?.data);
      if (serverErrs) {
        setErrors(serverErrs);
        toast.error(
          serverErrs._form ||
            "Some fields need to be corrected before we can accept your inquiry."
        );
      } else if (status && status >= 500) {
        setErrors({ _form: "Our server had trouble accepting your inquiry. Please try again shortly." });
        toast.error("Server error — please try again.");
      } else if (err?.code === "ECONNABORTED") {
        setErrors({ _form: "The request timed out. Please check your connection and try again." });
        toast.error("Request timed out.");
      } else {
        setErrors({
          _form:
            "We couldn't submit your inquiry. Please try again, or email us directly at " +
            COMPANY.irEmail + ".",
        });
        toast.error("Submission failed.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      {/* Hero */}
      <section className="bg-[#14110E] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
            <span className="rule-orange" /> {CONTACT.eyebrow}
          </div>
          <h1 className="mt-6 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-4xl">
            {CONTACT.headline}
          </h1>
          <p className="mt-8 text-[#F5F2EB] leading-relaxed max-w-3xl">{CONTACT.lead}</p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
            <div className="border border-[#4A3D30] px-6 py-5 flex items-start gap-4" data-testid="contact-ir-block">
              <Mail className="h-5 w-5 text-[#F26A1B] mt-1 flex-shrink-0" />
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#A79E93]">
                  Direct Investor Contact
                </div>
                <a
                  href={`mailto:${COMPANY.irEmail}`}
                  className="text-white text-lg font-semibold hover:text-[#F26A1B] transition-colors break-all"
                  data-testid="contact-ir-email"
                >
                  {COMPANY.irEmail}
                </a>
              </div>
            </div>
            <div className="border border-[#4A3D30] px-6 py-5 flex items-start gap-4" data-testid="contact-address-block">
              <MapPin className="h-5 w-5 text-[#F26A1B] mt-1 flex-shrink-0" />
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#A79E93]">
                  Corporate Address
                </div>
                <address
                  className="not-italic text-white text-sm leading-relaxed mt-1"
                  data-testid="contact-corporate-address"
                >
                  {COMPANY.address.line1}
                  <br />
                  {COMPANY.address.line2}
                  <br />
                  {COMPANY.address.city}, {COMPANY.address.state} {COMPANY.address.postalCode}
                </address>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
          <span className="rule-orange" /> How We Can Connect
        </div>
        <h2 className="mt-6 font-display text-3xl lg:text-4xl text-[#14110E] font-semibold tracking-tight max-w-3xl">
          Direct your inquiry to the appropriate area
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {CONTACT.areas.map((a, i) => (
            <div
              key={a.title}
              className="border border-[#DED6C6] p-6 hover:border-[#14110E] transition-colors"
              data-testid={`contact-area-${i}`}
            >
              <h3 className="font-display text-lg text-[#14110E] font-semibold">{a.title}</h3>
              <p className="mt-3 text-sm text-[#4A3D30] leading-relaxed">{a.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Form */}
      <section className="bg-[#F4F1EA] border-y border-[#DED6C6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
              <span className="rule-orange" /> Send an Inquiry
            </div>
            <h2 className="mt-6 font-display text-3xl lg:text-4xl text-[#14110E] font-semibold tracking-tight">
              Contact the Company
            </h2>
            <p className="mt-6 text-[#4A3D30] leading-relaxed">
              Provide enough detail for the Company to evaluate and route your inquiry appropriately.
              Only Name, Email and Message are required — the rest is optional context.
            </p>

            <div className="mt-6 border border-[#F26A1B]/30 bg-[#FFF3F0] p-4 flex items-start gap-3" data-testid="contact-mock-notice">
              <Sparkles className="h-4 w-4 text-[#F26A1B] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#7A2E14] leading-relaxed">
                <strong className="font-semibold">Email notifications are pending API key.</strong>{" "}
                Submissions are securely stored and will be reviewed by the Investor Relations team.
                Automated email delivery to {COMPANY.irEmail} will be activated once an email
                provider API key is provided.
              </p>
            </div>

            <div className="mt-8">
              <div className="eyebrow text-[#A79E93]">Strategic Interests</div>
              <ul className="mt-4 space-y-2">
                {CONTACT.interests.map((i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#14110E]">
                    <CheckCircle2 className="h-4 w-4 text-[#F26A1B] mt-0.5 flex-shrink-0" />
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7">
            {success ? (
              <SuccessPanel
                info={success}
                onNew={() => setSuccess(null)}
              />
            ) : (
              <form
                onSubmit={submit}
                noValidate
                className="bg-white border border-[#DED6C6] p-8 lg:p-10 space-y-5"
                data-testid="contact-form"
              >
                {errors._form && (
                  <div
                    role="alert"
                    data-testid="contact-form-error"
                    className="flex items-start gap-3 border border-[#F26A1B]/40 bg-[#FFF3F0] p-4 text-sm text-[#7A2E14]"
                  >
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#F26A1B]" />
                    <span>{errors._form}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Name *" testId="contact-field-name" error={errors.name} errorTestId="contact-error-name">
                    <input
                      value={form.name}
                      onChange={change("name")}
                      className={cls(errors.name)}
                      aria-invalid={!!errors.name}
                      data-testid="contact-input-name"
                    />
                  </Field>
                  <Field label="Company" testId="contact-field-company" error={errors.company}>
                    <input
                      value={form.company}
                      onChange={change("company")}
                      className={cls(errors.company)}
                      data-testid="contact-input-company"
                    />
                  </Field>
                  <Field label="Email Address *" testId="contact-field-email" error={errors.email} errorTestId="contact-error-email">
                    <input
                      type="email"
                      value={form.email}
                      onChange={change("email")}
                      className={cls(errors.email)}
                      aria-invalid={!!errors.email}
                      autoComplete="email"
                      data-testid="contact-input-email"
                    />
                  </Field>
                  <Field label="Telephone" testId="contact-field-phone" error={errors.telephone}>
                    <input
                      value={form.telephone}
                      onChange={change("telephone")}
                      className={cls(errors.telephone)}
                      data-testid="contact-input-phone"
                    />
                  </Field>
                </div>

                <Field label="Inquiry Type" testId="contact-field-inquirytype" error={errors.inquiry_type}>
                  <select
                    value={form.inquiry_type}
                    onChange={change("inquiry_type")}
                    className={cls(errors.inquiry_type)}
                    data-testid="contact-input-inquirytype"
                  >
                    <option value="">Select an inquiry type (optional)</option>
                    {CONTACT.inquiryTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Subject" testId="contact-field-subject" error={errors.subject}>
                  <input
                    value={form.subject}
                    onChange={change("subject")}
                    className={cls(errors.subject)}
                    data-testid="contact-input-subject"
                  />
                </Field>

                <Field label="Message *" testId="contact-field-message" error={errors.message} errorTestId="contact-error-message">
                  <textarea
                    rows={6}
                    value={form.message}
                    onChange={change("message")}
                    className={cls(errors.message)}
                    aria-invalid={!!errors.message}
                    data-testid="contact-input-message"
                  />
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-gradient rounded-full disabled:brightness-75 disabled:cursor-not-allowed px-6 py-3.5 text-sm font-semibold tracking-wide transition-colors inline-flex items-center gap-2"
                  data-testid="contact-submit-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      Submit Inquiry <ArrowUpRight className="h-4 w-4" />
                    </>
                  )}
                </button>

                <p className="text-xs text-[#A79E93] leading-relaxed pt-2" data-testid="contact-form-disclaimer">
                  {CONTACT.disclaimer}
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function SuccessPanel({ info, onNew }) {
  return (
    <div
      className="bg-white border border-[#DED6C6] p-8 lg:p-10"
      data-testid="contact-success"
      role="status"
      aria-live="polite"
    >
      <div className="h-12 w-12 rounded-full bg-[#FFF3F0] flex items-center justify-center">
        <CheckCircle2 className="h-6 w-6 text-[#F26A1B]" />
      </div>
      <h3 className="mt-6 font-display text-2xl lg:text-3xl text-[#14110E] font-semibold">
        Inquiry received.
      </h3>
      <p className="mt-4 text-[#4A3D30] leading-relaxed">
        Thank you for reaching out to Trafalgar International, Inc. Your submission has been
        securely stored and will be reviewed by the Investor Relations team.
      </p>
      <div className="mt-6 border border-[#DED6C6] bg-[#F4F1EA] p-4">
        <div className="eyebrow text-[#A79E93]">Reference</div>
        <div className="mt-2 font-mono text-xs text-[#14110E] break-all" data-testid="contact-success-id">
          {info.id}
        </div>
        <div className="mt-2 font-mono text-[11px] text-[#A79E93]">
          Received {info.received_at}
        </div>
      </div>
      <p className="mt-6 text-xs text-[#8A8075] leading-relaxed">
        Automated email delivery to ernesto.gomez@trafalgar.mx will be activated once an email
        provider API key is provided. In the meantime, you may reach the team directly at{" "}
        <a href="mailto:ernesto.gomez@trafalgar.mx" className="text-[#14110E] font-semibold hover:text-[#F26A1B]">
          ernesto.gomez@trafalgar.mx
        </a>
        .
      </p>
      <button
        type="button"
        onClick={onNew}
        className="mt-8 inline-flex items-center gap-2 border border-[#14110E] text-[#14110E] hover:bg-[#14110E] hover:text-white px-5 py-3 text-sm font-semibold tracking-wide transition-colors"
        data-testid="contact-success-new"
      >
        Send another inquiry
      </button>
    </div>
  );
}

function Field({ label, children, testId, error, errorTestId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="block eyebrow text-[#A79E93] mb-2">{label}</span>
      {children}
      {error && (
        <span
          role="alert"
          data-testid={errorTestId}
          className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#B91C1C]"
        >
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </span>
      )}
    </label>
  );
}

function cls(hasError) {
  return (
    "w-full border px-4 py-3 text-sm bg-white transition-colors focus:outline-none " +
    (hasError
      ? "border-[#B91C1C] focus:border-[#B91C1C]"
      : "border-[#DED6C6] focus:border-[#14110E]")
  );
}
